import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowRight, BookOpen, Globe2, GraduationCap, Heart, Monitor, Mail, FilePen, ClipboardCheck, CreditCard, CalendarHeart, Award } from "lucide-react";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/ab8e43c6-7f67-4643-8684-436daab5f46e/images/eb3e83de3c318b224580a745d1d672a7ba6b119fe3c4914fe8275a756e4763db.png";
const LIB_IMG = "https://images.unsplash.com/photo-1767102060241-130cb9260718?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXB0b3AlMjBsaWJyYXJ5fGVufDB8fHx8MTc3ODQyMjMzNXww&ixlib=rb-4.1.0&q=85";
const FAM_IMG = "https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtdWx0aWN1bHR1cmFsJTIwZmFtaWx5JTIwc21pbGluZyUyMGhvbWV8ZW58MHx8fHwxNzc4NDIyMzM1fDA&ixlib=rb-4.1.0&q=85";
const DESK_IMG = "https://images.unsplash.com/photo-1776057441344-38e0587ad1e6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&auto=format&fit=crop&ixlib=rb-4.1.0";

const PROGRAM_KEYS = [
  { key: "fluent_track", icon: Heart, color: "var(--primary)" },
  { key: "bridge_track", icon: BookOpen, color: "var(--brand-mint)" },
];

export default function LandingPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [studentLifeStudentId, setStudentLifeStudentId] = useState(null);

  // If a parent is already logged in, pre-fetch their enrolled student so the "Vie scolaire"
  // pillar can deep-link directly into the SIS instead of bouncing to login.
  useEffect(() => {
    let cancelled = false;
    if (user && user.role === "parent") {
      api.get("/enrollments/mine")
        .then(r => {
          if (cancelled) return;
          const enr = (r.data || []).find(e => e.student_id);
          if (enr) setStudentLifeStudentId(enr.student_id);
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [user]);

  const goAdmissions = (e) => {
    e.preventDefault();
    if (!user) return nav("/register");
    if (user.role === "parent") return nav("/parent");
    return nav("/admin");
  };

  const goStudentLife = (e) => {
    e.preventDefault();
    if (!user) return nav("/login?redirect=studentlife");
    if (user.role !== "parent") return nav("/admin");
    if (studentLifeStudentId) return nav(`/parent/student/${studentLifeStudentId}?tab=schedule`);
    toast.info(lang === "fr"
      ? "Aucun élève inscrit pour l'instant — finissez d'abord l'inscription pour accéder à la Vie scolaire."
      : "No enrolled student yet — please complete enrollment first to access Student Life.");
    nav("/parent");
  };

  return (
    <div className="bg-paper relative overflow-hidden" data-testid="landing-page">
      {/* Decorative brand blobs */}
      <div className="brand-blob" style={{ background: "hsl(var(--brand-mint))", width: 320, height: 320, top: 80, left: -120 }} />
      <div className="brand-blob" style={{ background: "hsl(var(--brand-yellow))", width: 280, height: 280, top: 600, right: -100, opacity: 0.35 }} />
      <div className="brand-blob" style={{ background: "hsl(var(--brand-lavender))", width: 260, height: 260, top: 1400, left: -80, opacity: 0.3 }} />

      {/* Hero — Bento layout: emotional headline + family photo + 2 massive pillars */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-16 lg:pt-14 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">

          {/* Big text panel (left, 7 cols) */}
          <div className="lg:col-span-7 relative bg-white border border-border/60 rounded-3xl p-8 lg:p-12 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#2D78F6]/12 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-[#F8CD46]/15 blur-3xl pointer-events-none" />
            <h1
              className="font-display font-semibold tracking-tighter text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.02] relative"
              data-testid="hero-title"
            >
              {t("landing.heroPlatformIntro")}
            </h1>
            <p
              className="mt-6 font-display font-medium tracking-tight text-foreground/75 text-lg sm:text-xl leading-snug max-w-lg relative"
              data-testid="hero-lead"
            >
              {t("landing.heroLead")}
            </p>
            <p
              className="mt-5 text-xs sm:text-sm text-muted-foreground max-w-md relative"
              data-testid="hero-discover"
            >
              {t("landing.heroDiscoverHint")
                .split("ofalycee.fr")
                .reduce((acc, part, i, arr) => {
                  acc.push(<React.Fragment key={`p-${i}`}>{part}</React.Fragment>);
                  if (i < arr.length - 1) {
                    acc.push(
                      <a key={`l-${i}`} href="https://ofalycee.fr" target="_blank" rel="noopener noreferrer" className="text-[#2D78F6] font-medium underline hover:opacity-80 transition-opacity">
                        ofalycee.fr
                      </a>
                    );
                  }
                  return acc;
                }, [])}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 relative">
              <Link to="/inquiry" data-testid="hero-cta-inquiry">
                <Button size="lg" variant="outline" className="rounded-full px-7 border-2 hover:-translate-y-0.5 hover:shadow-md transition-all">
                  {t("landing.ctaInquiry")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero photo panel (right, 5 cols) — French + American flags: bilingual identity */}
          <div className="lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[260px] lg:min-h-0 group">
            <img
              src="https://customer-assets.emergentagent.com/job_ofa-enroll/artifacts/y0ov8rf7_iStock-1089810846.jpg"
              alt={lang === "fr" ? "Drapeaux français et américain" : "French and American flags"}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#14274B]/75 via-[#14274B]/15 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7 text-white">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold mb-2 backdrop-blur bg-white/10 px-2.5 py-1 rounded-full border border-white/20">
                <Monitor className="w-3 h-3" /> {lang === "fr" ? "École 100% en ligne" : "Fully online school"}
              </div>
              <p className="font-display font-medium text-lg leading-snug">
                {lang === "fr"
                  ? "Une école américaine, accessible depuis la France."
                  : "An American school, accessible from France."}
              </p>
            </div>
          </div>

          {/* Pillar 1 — Admissions (full width — SIS pillar removed for RGPD compliance) */}
          <a
            href="/register"
            onClick={goAdmissions}
            data-testid="hub-pillar-admissions"
            className="lg:col-span-12 group relative rounded-3xl bg-[#14274B] text-white p-7 lg:p-9 overflow-hidden hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#14274B]/25 transition-all cursor-pointer block"
          >
            <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#2D78F6]/30 blur-3xl pointer-events-none group-hover:bg-[#2D78F6]/45 transition-colors" />
            <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full bg-[#926EF7]/20 blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#F8CD46] mb-3">
                  <FilePen className="w-3.5 h-3.5" /> {lang === "fr" ? "Admissions" : "Admissions"}
                </div>
                <h3 className="font-display font-semibold text-3xl lg:text-4xl tracking-tight leading-[1.05] mb-3">
                  {lang === "fr" ? "Inscrire votre enfant" : "Enroll your child"}
                </h3>
                <p className="text-white/75 max-w-md leading-snug text-sm lg:text-base">
                  {lang === "fr"
                    ? "Démarrez une candidature en quelques minutes."
                    : "Start an application in minutes."}
                </p>
              </div>
              <div className="shrink-0 w-12 h-12 rounded-full bg-[#2D78F6] text-white flex items-center justify-center group-hover:translate-x-1 group-hover:scale-110 transition-all shadow-lg shadow-[#2D78F6]/30">
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </a>

        </div>
      </section>

      {/* Journey strip — from inquiry to school life */}
      <section className="relative bg-card border-y border-border" data-testid="journey-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="overline mb-3">{t("landing.journeyOverline")}</p>
              <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-2xl">
                {t("landing.journeyTitle")}
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
              {t("landing.journeySubtitle")}
            </p>
          </div>

          <div className="relative">
            {/* Connecting line on desktop */}
            <div className="hidden lg:block absolute top-7 left-[7%] right-[7%] h-px bg-gradient-to-r from-primary/20 via-accent/40 to-primary/20" />
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
              {[
                { k: "journeyStep1", icon: FilePen, color: "var(--brand-mint)" },
                { k: "journeyStep2", icon: ClipboardCheck, color: "var(--brand-lavender)" },
                { k: "journeyStep4", icon: CreditCard, color: "var(--brand-yellow)" },
              ].map(({ k, icon: Icon, color }, i) => (
                <li
                  key={k}
                  data-testid={`journey-${k}`}
                  className="bg-paper border border-border rounded-2xl p-5 text-center hover:shadow-md hover:-translate-y-0.5 transition-all relative"
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ring-4 ring-card"
                    style={{ background: `hsl(${color} / 0.12)` }}
                  >
                    <Icon className="w-6 h-6" strokeWidth={1.75} style={{ color: `hsl(${color})` }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display font-semibold text-base leading-tight">
                    {t(`landing.${k}`)}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 leading-tight">
                    {t(`landing.${k}Sub`)}
                  </div>
                </li>
              ))}
            </ol>

            {/* Primary CTA — Start enrollment */}
            <div className="mt-10 flex flex-col items-center gap-3" data-testid="journey-cta">
              <Link to="/register" data-testid="journey-cta-signup">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-9 py-6 shadow-md text-base font-display font-semibold">
                  {lang === "fr" ? "Commencer ma demande d'admission" : "Start my admission application"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                {lang === "fr" ? "Création de votre espace famille en 1 minute" : "Create your family portal in 1 minute"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual feature row */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid lg:grid-cols-[2fr_3fr] gap-8 lg:gap-12 items-center bg-card border border-border rounded-2xl overflow-hidden">
          <div className="order-2 lg:order-1 p-8 lg:p-12">
            <h3 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter leading-[1.1] mb-6">
              {lang === "fr"
                ? "Une exigence bilingue qui se construit progressivement."
                : "A bilingual rigor that builds progressively."}
            </h3>
            <ul className="space-y-2.5 text-base">
              {(lang === "fr"
                ? [
                    "Professeurs natifs",
                    "Petits effectifs (4 à 8 élèves)",
                    "32 semaines de cours (2 × 45 min hebdomadaire)",
                    "High School Diploma américain",
                    "Emploi du temps adapté",
                  ]
                : [
                    "Native teachers",
                    "Small groups (4 to 8 students)",
                    "32 weeks of class (2 × 45 min weekly)",
                    "American High School Diploma",
                    "Flexible schedule",
                  ]
              ).map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  <span className="font-medium text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 lg:order-2 relative h-56 lg:h-72">
            <img
              src="https://customer-assets.emergentagent.com/job_ofa-enroll/artifacts/pxi83zml_iStock-2217341234.jpg"
              alt={lang === "fr" ? "Élève rêveuse à son bureau" : "Student daydreaming at her desk"}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 25%" }}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 text-center relative">
        <div className="brand-blob" style={{ background: "hsl(var(--brand-yellow))", width: 240, height: 240, top: 0, left: "50%", transform: "translateX(-50%)", opacity: 0.25 }} />
        <p className="overline mb-4 relative">{lang === "fr" ? "Premier contact" : "First contact"}</p>
        <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter max-w-2xl mx-auto relative">{lang === "fr" ? "Démarrez en quelques minutes." : "Start in just a few minutes."}</h2>
        <div className="mt-10 flex justify-center gap-3 flex-wrap relative">
          <Link to="/inquiry" data-testid="cta-bottom-inquiry">
            <Button size="lg" variant="outline" className="rounded-full px-7 border-2">{t("landing.ctaInquiry")}</Button>
          </Link>
          <Link to="/register" data-testid="cta-bottom-signup">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 shadow-md">
              {lang === "fr" ? "Commencer ma demande d'admission" : "Start my admission application"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
